import type { TreasureKind } from "@xiuxian/game-protocol";
import {
  formatTreasureName,
  formatTreasureShortEffect,
} from "./i18n/game-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "./i18n/index.js";

export interface TreasureUiConfig {
  kind: TreasureKind;
  name: string;
  shortEffect: string;
  iconPath: string;
  imagePath: string;
  color: number;
  glowColor: number;
}

export interface TreasureSlotView {
  index: number;
  kind: TreasureKind | null;
  label: string;
  iconPath: string | null;
  imagePath: string | null;
}

const TREASURE_ICON_BASE_PATH = "/treasures";
const MIN_TREASURE_SLOT_COUNT = 1;
const MAX_TREASURE_SLOT_COUNT = 3;

type TreasureVisualConfig = Omit<TreasureUiConfig, "name" | "shortEffect">;

export const TREASURE_UI_CONFIGS: Record<TreasureKind, TreasureVisualConfig> = {
  qiankun_ruler: {
    kind: "qiankun_ruler",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/qiankun-ruler.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/qiankun-ruler.png`,
    color: 0xffd166,
    glowColor: 0xfff1a8,
  },
  hunyuan_fan: {
    kind: "hunyuan_fan",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/hunyuan-fan.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/hunyuan-fan.png`,
    color: 0x9ce6ff,
    glowColor: 0xd8f7ff,
  },
  spirit_gourd: {
    kind: "spirit_gourd",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/spirit-gourd.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/spirit-gourd.png`,
    color: 0x6ae3b1,
    glowColor: 0xc8ffe6,
  },
  thunder_sword_box: {
    kind: "thunder_sword_box",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/thunder-sword-box.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/thunder-sword-box.png`,
    color: 0xb7f2ff,
    glowColor: 0xffffff,
  },
  myriad_sword_token: {
    kind: "myriad_sword_token",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/myriad-sword-token.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/myriad-sword-token.png`,
    color: 0xf7e4b5,
    glowColor: 0xfff7dc,
  },
  wind_thunder_boots: {
    kind: "wind_thunder_boots",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/wind-thunder-boots.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/wind-thunder-boots.png`,
    color: 0xd8b4fe,
    glowColor: 0xf2e7ff,
  },
  black_tortoise_armor: {
    kind: "black_tortoise_armor",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/black-tortoise-armor.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/black-tortoise-armor.png`,
    color: 0xa7f3d0,
    glowColor: 0xdcfff0,
  },
  ascension_pearl: {
    kind: "ascension_pearl",
    iconPath: `${TREASURE_ICON_BASE_PATH}/art/ascension-pearl.png`,
    imagePath: `${TREASURE_ICON_BASE_PATH}/art-large/ascension-pearl.png`,
    color: 0xfff7ad,
    glowColor: 0xffffff,
  },
};

export const TREASURE_KINDS = Object.keys(
  TREASURE_UI_CONFIGS,
) as TreasureKind[];

export function getTreasureUiConfig(
  kind: TreasureKind,
  locale: Locale = DEFAULT_LOCALE,
): TreasureUiConfig {
  return {
    ...TREASURE_UI_CONFIGS[kind],
    name: formatTreasureName(locale, kind),
    shortEffect: formatTreasureShortEffect(locale, kind),
  };
}

export function getTreasureTextureKey(kind: TreasureKind): string {
  return `treasure-${kind}`;
}

export function createTreasureSlotViews(
  kinds: readonly TreasureKind[],
  slotCount: number,
  locale: Locale = DEFAULT_LOCALE,
): TreasureSlotView[] {
  const normalizedSlotCount = Math.max(
    MIN_TREASURE_SLOT_COUNT,
    Math.min(MAX_TREASURE_SLOT_COUNT, Math.trunc(slotCount)),
  );
  return Array.from({ length: normalizedSlotCount }, (_, index) => {
    const kind = kinds[index] ?? null;
    if (!kind) {
      return {
        index,
        kind: null,
        label: locale === "en" ? "Empty slot" : "空宝位",
        iconPath: null,
        imagePath: null,
      };
    }

    const config = getTreasureUiConfig(kind, locale);
    return {
      index,
      kind,
      label: config.name,
      iconPath: config.iconPath,
      imagePath: config.imagePath,
    };
  });
}
